import React, { useMemo } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import styled, { css, FlattenSimpleInterpolation } from 'styled-components'
import { CoverLetterMenu } from '@app.feature/coverletter/coverletter.write/screen/rightSection/menus/CoverLetterSearch/store/coverLetter'

export type TabType = {
  id: number
  name: string
  count?: number
  size?: string
  href?: {
    query: {
      [key: string]: string
    }
  }
  value?: string
  selectedList?: string[]
}

type TProps = {
  data: TabType[]
  initialTab?: TabType
  queryKey?: string
  className?: string
  onChange?: (tab: TabType) => void
  type?: 'query' | 'value'
  value?: CoverLetterMenu
}

const CardTabs: React.FC<TProps> = ({
  data,
  initialTab,
  queryKey = 'tab',
  className,
  onChange,
  type = 'query',
  value,
}) => {
  const router = useRouter()
  const selectedTab = useMemo(() => {
    // query 타입일 때 선택된 탭의 이름
    const selectedTabName =
      type === 'query' ? `${router.query[queryKey]}` : null
    // 선택된 탭 찾기
    const findSelectedTab = (tab: TabType) => {
      if (type === 'query' && tab.selectedList) {
        // query 타입이면서 selectedList가 있는 경우
        return tab.selectedList.includes(selectedTabName as string)
      }
      if (type === 'query') {
        // query 타입이면서 selectedList가 없는 경우
        return tab.href?.query[queryKey] === selectedTabName
      }
      if (type === 'value') {
        // value 타입
        return tab.value === value
      }
      return false
    }
    // 데이터 배열에서 선택된 탭 찾기
    const selectedTab = data.find(findSelectedTab)
    // 선택된 탭이 없으면 초기 탭 반환
    return selectedTab || initialTab
  }, [type, value, router, queryKey, data, initialTab])

  const handleClick = (tab: TabType) => {
    if (type === 'query') {
      router.push({
        query: {
          ...router.query,
          ...tab.href?.query,
        },
      })
    }

    onChange && onChange(tab)
  }

  return (
    <StyledWrapper className={className}>
      {data.map((tab, idx) => (
        <Link
          key={idx}
          href={
            type === 'query'
              ? {
                  query: {
                    ...router.query,
                    ...tab.href?.query,
                  },
                }
              : ''
          }
        >
          <a>
            <StyledTab
              className="tab"
              onClick={() => handleClick(tab)}
              customCss={css`
                width: ${tab.size};
              `}
              data-active={selectedTab?.id === tab.id}
            >
              {tab.name} {tab.count !== undefined && `(${tab.count || 0})`}
            </StyledTab>
          </a>
        </Link>
      ))}
    </StyledWrapper>
  )
}

export default CardTabs

const StyledWrapper = styled.div`
  display: flex;
  height: 50px;

  a {
    border: 1px solid #dddddd;
    border-right: none;
    box-sizing: border-box;
    flex: 1;

    &:last-child {
      border-right: 1px solid #dddddd;
    }
  }
`

const StyledTab = styled.button<{
  customCss?: FlattenSimpleInterpolation
}>`
  border: none;
  background: none;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  font-size: 16px;

  &[data-active='true'] {
    border: 1px solid #01a0ff;
    color: #01a0ff;
  }

  ${({ customCss }) => customCss}
`
